// Pure state resolver for the Order feedback Overview page.
// Modelled on src/scenes/LiveUpdates/components/overview/providerStatus.ts
// (open PR https://github.com/thanx/thanx-merchant-ui/pull/4075): a pure
// function from real signals to a content descriptor of i18n keys and
// booleans. Never return a literal display string from this module — the
// Hero/FeedbackSetupSection components resolve every *Key field through
// `locales/orderFeedback`.

import type { ThirdPartyFeedbackProvider } from 'api/integrationsConfig'
import { owningThirdPartyProvider } from '../feedbackOwnership'

export type RaiCoverage = 'none' | 'partial' | 'full'

export type OverviewStateKind =
  | 'new'
  | 'thanx'
  | 'thirdparty'
  | 'rai_thanx'
  | 'rai_all'
  | 'rai_thirdparty'

// `allow_written_feedback_collection` (nexus PR #1938, not yet merged) is the
// real source for `isCollectingWrittenFeedback`. This module intentionally
// does not import a selector for it — that wiring is follow-up work once the
// nucleus field exists. Every signal is `| undefined` distinctly from its
// settled values, because "not loaded yet" and "loaded and false" must not
// resolve to the same state (see the loading-state tests).
export type OverviewSignals = {
  isCollectingWrittenFeedback: boolean | undefined
  thirdPartyProvider: ThirdPartyFeedbackProvider | null | undefined
  raiCoverage: RaiCoverage | undefined
}

export type OverviewHeroContent = {
  // False only for `thirdparty`, whose hero region renders no headline, lede,
  // badge, or CTA — see resolveOverviewState's state-shape comment.
  visible: boolean
  showLocationBadge: boolean
  // True for the three RecoveryAI states. Those get a structurally different
  // hero card in the design — wider right column, flat tint instead of the
  // gradient, tighter panel padding, a drop shadow, and a still image where
  // the other states run the animated stage. Carried on the descriptor so the
  // component reads one flag rather than re-deriving it from `kind`, matching
  // how every other per-state variation in this module is expressed.
  useRaiVariant: boolean
  badgeKey?: string
  headlineKey?: string
  ledeKey?: string
  nudgeKey?: string
  // Full i18n keys, in display order (primary CTA first).
  ctaKeys: string[]
}

export type OverviewFeedbackSetupContent = {
  // False for rai_thanx and rai_all: RecoveryAI is already covering every
  // digital order, so there's nothing left to set up.
  visible: boolean
  // The design gives this card three shapes, not two:
  //   'stacked'     — badge + title, then the body and an outlined button on
  //                   one row (new). That button stays a button because it is
  //                   the only turn-it-on affordance the page offers.
  //   'illustrated' — badge + title, then an illustration beside the body,
  //                   with the CTA demoted to a right-aligned text link
  //                   (thanx). Setup is already done, so it is a shortcut.
  //   'compact'     — status line + sub-copy, no badge, title or CTA
  //                   (thirdparty, rai_thirdparty).
  variant: 'stacked' | 'illustrated' | 'compact'
  badgeKey?: string
  titleKey?: string
  labelKey?: string
  statusKey?: string
  bodyKey?: string
  ctaKey?: string
}

export type OverviewHowItWorksStep = {
  titleKey: string
  bodyKey: string
}

export type OverviewHowItWorksContent = {
  // True only for 'new' and 'thanx' — mirrors the top-level showHowItWorks
  // flag; kept as its own descriptor (rather than the boolean alone) so the
  // component reads i18n keys from here instead of building them itself.
  visible: boolean
  headingKey?: string
  // The intro takes three keys because its closing clause is a link onto the
  // Settings tab: the sentence up to that link, the link's own label, and the
  // punctuation that closes the sentence after it.
  introKey?: string
  introLinkKey?: string
  introSuffixKey?: string
  steps: OverviewHowItWorksStep[]
}

export type OverviewPromoContent = {
  // False for rai_thanx and rai_all: RecoveryAI is already running, so there
  // is nothing left to promote.
  visible: boolean
  headingKey?: string
  // Two bullets where RecoveryAI is pitched on its own, three where it is
  // pitched alongside an existing third-party tool.
  bulletKeys: string[]
  badgeKey?: string
  ctaKey?: string
}

export type OverviewContent = {
  kind: 'loading' | OverviewStateKind
  // Derived directly from the collecting + coverage signals (see below),
  // never from a per-state lookup — the rule holds for all 12 documented
  // combinations. Full RAI coverage hides the tab even when Thanx is
  // otherwise collecting comments, because RecoveryAI resolves everything
  // automatically at that point — there's no separate inbox left to work.
  feedbackTabVisible: boolean
  // v67 HIDES the Settings tab (it never disables one) in the three states
  // where Thanx's own written-feedback collection isn't what Settings
  // configures: a third-party tool owns feedback (`thirdparty`,
  // `rai_thirdparty`), or RecoveryAI already covers every location
  // (`rai_all`).
  settingsTabVisible: boolean
  // True for the five input combinations the design never specified.
  // "Thanx collection wins the overlap" is the default pending design
  // confirmation — see resolveStateKind.
  isUnconfirmedDefault: boolean
  // The tool that OWNS feedback, not merely a connected one: null while Thanx
  // collects, so no pane can name a tool the merchant is not using.
  thirdPartyProvider: ThirdPartyFeedbackProvider | null
  showCommentsOffBanner: boolean
  showPromoBlock: boolean
  showHowItWorks: boolean
  hero: OverviewHeroContent
  feedbackSetup: OverviewFeedbackSetupContent
  promo: OverviewPromoContent
  howItWorks: OverviewHowItWorksContent
}

const LOADING_CONTENT: OverviewContent = {
  kind: 'loading',
  feedbackTabVisible: false,
  // Both tab flags stay false until the signals settle: a tab that appears
  // after load is a smaller surprise than one that disappears out from under
  // a click the viewer has already made.
  settingsTabVisible: false,
  isUnconfirmedDefault: false,
  thirdPartyProvider: null,
  showCommentsOffBanner: false,
  showPromoBlock: false,
  showHowItWorks: false,
  hero: {
    visible: false,
    showLocationBadge: false,
    useRaiVariant: false,
    ctaKeys: [],
  },
  feedbackSetup: { visible: false, variant: 'stacked' },
  promo: { visible: false, bulletKeys: [] },
  howItWorks: { visible: false, steps: [] },
}

// The 12-combination mapping is settled by product; every branch below
// mirrors one row of that mapping exactly.
const resolveStateKind = (
  isCollecting: boolean,
  owner: ThirdPartyFeedbackProvider | null,
  raiCoverage: RaiCoverage
): OverviewStateKind => {
  if (owner !== null) {
    // partial or full: RecoveryAI intercepts digital orders regardless of
    // how much of the guest's overall feedback picture RAI itself covers —
    // the state kind doesn't distinguish partial from full here.
    return raiCoverage === 'none' ? 'thirdparty' : 'rai_thirdparty'
  }

  if (isCollecting) {
    // Thanx collection wins the overlap whenever RAI is off or partial/full
    // alongside a connected third party (rows 4, 7, 8 — unconfirmed defaults
    // when a third party is also connected; confirmed when it's not).
    if (raiCoverage === 'none') return 'thanx'
    if (raiCoverage === 'partial') return 'rai_thanx'
    return 'rai_all'
  }

  // Not collecting, no third party: nothing else is gathering feedback for
  // RecoveryAI to act on, so raiCoverage alone decides. `none` is the
  // confirmed 'new' state; partial/full are unconfirmed defaults (rows 11,
  // 12) — the design never specified this combination.
  if (raiCoverage === 'none') return 'new'
  return raiCoverage === 'partial' ? 'rai_thanx' : 'rai_all'
}

// A connected tool that does not own feedback is the overlap the design never
// settled; so is RecoveryAI running with nothing collecting feedback at all.
const isUnconfirmedDefault = (
  isCollecting: boolean,
  thirdPartyProvider: ThirdPartyFeedbackProvider | null,
  owner: ThirdPartyFeedbackProvider | null,
  raiCoverage: RaiCoverage
): boolean =>
  (thirdPartyProvider !== null && owner === null) ||
  (!isCollecting && thirdPartyProvider === null && raiCoverage !== 'none')

const buildHero = (kind: OverviewStateKind): OverviewHeroContent => {
  const base = `states.${kind}.hero`
  switch (kind) {
    case 'thirdparty':
      return {
        visible: false,
        showLocationBadge: false,
        useRaiVariant: false,
        ctaKeys: [],
      }
    case 'new':
    case 'thanx':
      return {
        visible: true,
        showLocationBadge: false,
        useRaiVariant: false,
        headlineKey: `${base}.headline`,
        ledeKey: `${base}.lede`,
        ctaKeys: [`${base}.cta_primary`, `${base}.cta_secondary`],
      }
    case 'rai_thanx':
      return {
        visible: true,
        // The only state with a location count badge. Its absence is what
        // distinguishes full coverage from partial.
        showLocationBadge: true,
        useRaiVariant: true,
        badgeKey: `${base}.badge`,
        headlineKey: `${base}.headline`,
        ledeKey: `${base}.lede`,
        nudgeKey: `${base}.nudge`,
        ctaKeys: [`${base}.cta_primary`, `${base}.cta_secondary`],
      }
    case 'rai_all':
      return {
        visible: true,
        showLocationBadge: false,
        useRaiVariant: true,
        headlineKey: `${base}.headline`,
        ledeKey: `${base}.lede`,
        // Single CTA in this state only — "View escalations" doesn't apply
        // once the Feedback tab is hidden (full RAI coverage hides it).
        ctaKeys: [`${base}.cta_primary`],
      }
    case 'rai_thirdparty':
      return {
        visible: true,
        showLocationBadge: false,
        useRaiVariant: true,
        headlineKey: `${base}.headline`,
        ledeKey: `${base}.lede`,
        // Single CTA in this state only.
        ctaKeys: [`${base}.cta_primary`],
      }
  }
}

const buildPromo = (kind: OverviewStateKind): OverviewPromoContent => {
  const base = `states.${kind}.promo`
  switch (kind) {
    case 'new':
    case 'thanx':
      return {
        visible: true,
        headingKey: `${base}.heading`,
        bulletKeys: [`${base}.bullet_1`, `${base}.bullet_2`],
        badgeKey: `${base}.badge`,
        ctaKey: `${base}.cta`,
      }
    case 'thirdparty':
    case 'rai_thirdparty':
      return {
        visible: true,
        headingKey: `${base}.heading`,
        bulletKeys: [
          `${base}.bullet_1`,
          `${base}.bullet_2`,
          `${base}.bullet_3`,
        ],
        badgeKey: `${base}.badge`,
        ctaKey: `${base}.cta`,
      }
    case 'rai_thanx':
    case 'rai_all':
      return { visible: false, bulletKeys: [] }
  }
}

const buildFeedbackSetup = (
  kind: OverviewStateKind
): OverviewFeedbackSetupContent => {
  const base = `states.${kind}.feedback_setup`
  const full = {
    visible: true,
    badgeKey: `${base}.badge`,
    titleKey: `${base}.title`,
    labelKey: `${base}.label`,
    bodyKey: `${base}.body`,
    ctaKey: `${base}.cta`,
  }

  switch (kind) {
    case 'new':
      return { ...full, variant: 'stacked' }
    case 'thanx':
      return { ...full, variant: 'illustrated' }
    case 'thirdparty':
    case 'rai_thirdparty':
      return {
        visible: true,
        variant: 'compact',
        statusKey: `${base}.status`,
        bodyKey: `${base}.sub_copy`,
      }
    case 'rai_thanx':
    case 'rai_all':
      // RecoveryAI already covers every digital order in these two states —
      // nothing left to set up.
      return { visible: false, variant: 'stacked' }
  }
}

// Three flat step keys per state (step_1..step_3), not an array, matching how
// locales/orderFeedback.ts already stores them. Same copy in `new` and
// `thanx` today, but each state gets its own key path rather than a shared
// literal, so the two can diverge later without a resolver change.
const HOW_IT_WORKS_STEP_COUNT = 3

const buildHowItWorks = (
  kind: OverviewStateKind
): OverviewHowItWorksContent => {
  if (kind !== 'new' && kind !== 'thanx') return { visible: false, steps: [] }

  const base = `states.${kind}.how_it_works`
  return {
    visible: true,
    headingKey: `${base}.heading`,
    introKey: `${base}.intro`,
    introLinkKey: `${base}.intro_link`,
    introSuffixKey: `${base}.intro_suffix`,
    steps: Array.from({ length: HOW_IT_WORKS_STEP_COUNT }, (_, index) => {
      const step = index + 1
      return {
        titleKey: `${base}.step_${step}_title`,
        bodyKey: `${base}.step_${step}_body`,
      }
    }),
  }
}

export const resolveOverviewState = (
  signals: OverviewSignals
): OverviewContent => {
  const { isCollectingWrittenFeedback, thirdPartyProvider, raiCoverage } =
    signals

  if (
    isCollectingWrittenFeedback === undefined ||
    thirdPartyProvider === undefined ||
    raiCoverage === undefined
  ) {
    return LOADING_CONTENT
  }

  const owner = owningThirdPartyProvider(
    isCollectingWrittenFeedback,
    thirdPartyProvider
  )
  const kind = resolveStateKind(isCollectingWrittenFeedback, owner, raiCoverage)

  return {
    kind,
    // The rule holds across all 12 rows — derive it directly rather than
    // reading it back off a per-state table, so the two can never drift.
    // Full coverage (`rai_all`, reachable via one confirmed row and one
    // unconfirmed default) hides the tab even while collecting is true.
    feedbackTabVisible: isCollectingWrittenFeedback && raiCoverage !== 'full',
    // Keyed on `kind`, not on the raw signals: `kind` is the unit the design
    // specifies these six states in, and it already carries the documented
    // "Thanx collection wins the overlap" tie-break — so a merchant who is
    // both collecting AND running a third-party tool keeps the Settings tab
    // that the `thanx`/`rai_thanx` presentation it gets implies.
    settingsTabVisible:
      kind !== 'thirdparty' && kind !== 'rai_all' && kind !== 'rai_thirdparty',
    isUnconfirmedDefault: isUnconfirmedDefault(
      isCollectingWrittenFeedback,
      thirdPartyProvider,
      owner,
      raiCoverage
    ),
    thirdPartyProvider: owner,
    // Shown where Thanx itself is not collecting AND RecoveryAI is not
    // already handling order issues — `new` and `thirdparty` only. Keyed on
    // `kind` rather than on `!isCollectingWrittenFeedback`, which is what it
    // used to be: that also raised the banner in the RecoveryAI states, where
    // "you are not collecting feedback or comments via Thanx" reads as a
    // problem sitting directly beneath a hero saying RecoveryAI is catching
    // order issues in real time. Its second line names a connected tool and
    // is dropped when there isn't one.
    showCommentsOffBanner: kind === 'new' || kind === 'thirdparty',
    showPromoBlock: kind !== 'rai_thanx' && kind !== 'rai_all',
    showHowItWorks: kind === 'new' || kind === 'thanx',
    hero: buildHero(kind),
    feedbackSetup: buildFeedbackSetup(kind),
    promo: buildPromo(kind),
    howItWorks: buildHowItWorks(kind),
  }
}
